import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, UserPlus, Copy, Check, Loader2, Trash2, KeyRound, LogOut } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

export function SecurityPanel({ me }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [invites, setInvites] = useState([]);
  const [log, setLog] = useState([]);
  const [sessions, setSessions] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [outBusy, setOutBusy] = useState(false);

  const load = () => {
    api.get("/admin/invites").then((r) => setInvites(r.data.invites)).catch(() => {});
    api.get("/security/log").then((r) => setLog(r.data.log.slice(0, 12))).catch(() => {});
    api.get("/auth/sessions").then((r) => setSessions(r.data.count)).catch(() => {});
  };
  useEffect(load, []);

  const changePassword = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      const { data } = await api.post("/auth/password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success(
        data.sessions_ended
          ? `Password changed. ${data.sessions_ended} other session(s) were signed out.`
          : "Password changed."
      );
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not change the password");
    } finally {
      setPwBusy(false);
    }
  };

  const logOutEverywhere = async () => {
    setOutBusy(true);
    try {
      const { data } = await api.post("/auth/sessions/revoke-all");
      toast.success(data.ended ? `Signed out ${data.ended} other session(s).` : "No other sessions were open.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not sign the other sessions out");
    } finally {
      setOutBusy(false);
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    setCopied(false);
    try {
      const { data } = await api.post("/admin/invites", { email, role });
      setLink(data);
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create the invite");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/admin/invites/${id}`);
      toast.success("Invite revoked");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not revoke");
    }
  };

  return (
    <section className="rounded-lg border border-border bg-card p-6 lg:col-span-2" data-testid="security-panel">
      <div className="mb-3 flex items-center gap-2 text-muted-foreground">
        <ShieldCheck className="h-4 w-4" />
        <p className="text-xs font-semibold uppercase tracking-widest">Security &amp; access</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" data-testid="twofa-state"
          className={me?.totp_enabled ? "border-emerald-500/30 text-emerald-600 dark:text-emerald-400" : "border-amber-500/30 text-amber-600"}>
          Two-step verification {me?.totp_enabled ? "on" : "off"}
        </Badge>
        {me?.totp_enabled && (
          <Badge variant="outline" data-testid="recovery-left">{me.recovery_codes_left} recovery code(s) left</Badge>
        )}
        <Badge variant="outline">Public sign-up disabled</Badge>
        {sessions != null && (
          <Badge variant="outline" data-testid="session-count">{sessions} active session(s)</Badge>
        )}
      </div>
      <p className="mt-3 text-sm text-muted-foreground">
        Lost your phone? Two-step verification can only be reset from the server itself —
        <span className="font-mono text-foreground"> python manage.py reset-2fa --email you@domain</span> — and the reset is logged.
      </p>

      <form onSubmit={changePassword} className="mt-6 flex flex-wrap items-end gap-2 border-t border-border pt-5">
        <div className="min-w-[180px] flex-1 space-y-1">
          <Label htmlFor="currentPw">Current password</Label>
          <Input id="currentPw" type="password" autoComplete="current-password" required
            value={pw.current_password} data-testid="current-password"
            onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
        </div>
        <div className="min-w-[180px] flex-1 space-y-1">
          <Label htmlFor="newPw">New password (12+ characters)</Label>
          <Input id="newPw" type="password" autoComplete="new-password" required
            value={pw.new_password} data-testid="new-password"
            onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
        </div>
        <Button type="submit" variant="outline" disabled={pwBusy} data-testid="change-password">
          {pwBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />} Change password
        </Button>
        <Button type="button" variant="ghost" disabled={outBusy} onClick={logOutEverywhere} data-testid="logout-everywhere">
          {outBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LogOut className="mr-2 h-4 w-4" />} Log out everywhere
        </Button>
        <p className="w-full text-xs text-muted-foreground">
          Changing the password, resetting two-step verification or logging out everywhere ends every other
          signed-in session for this login.
        </p>
      </form>

      <form onSubmit={invite} className="mt-6 flex flex-wrap items-end gap-2 border-t border-border pt-5">
        <div className="min-w-[220px] flex-1 space-y-1">
          <Label htmlFor="inviteEmail">Invite a user</Label>
          <Input id="inviteEmail" type="email" placeholder="name@yourcompany.com" value={email}
            onChange={(e) => setEmail(e.target.value)} required data-testid="invite-email" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="inviteRole">Role</Label>
          <select id="inviteRole" value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role"
            className="flex h-9 rounded-md border border-input bg-background px-3 text-sm">
            <option value="admin">Admin — can publish</option>
            <option value="owner">Owner — full access</option>
            <option value="viewer">Viewer — read only</option>
          </select>
        </div>
        <Button type="submit" disabled={busy} data-testid="invite-create">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />} Create invite link
        </Button>
      </form>

      {link && (
        <div className="mt-4 rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="invite-link-box">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">Copy this now — shown once</p>
          <p className="mt-2 break-all font-mono text-xs">{link.invite_url}</p>
          <div className="mt-2 flex items-center gap-2">
            <Button size="sm" variant="outline" data-testid="invite-copy"
              onClick={() => { navigator.clipboard.writeText(link.invite_url); setCopied(true); }}>
              {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />} {copied ? "Copied" : "Copy link"}
            </Button>
            <span className="text-xs text-muted-foreground">
              For {link.email} · {link.role} · works once · expires in {link.expires_in_hours}h
            </span>
          </div>
        </div>
      )}

      {invites.length > 0 && (
        <div className="mt-5 space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Invites</p>
          {invites.map((i) => (
            <div key={i.id} className="flex items-center gap-2 text-sm" data-testid="invite-row">
              <span className="truncate">{i.email}</span>
              <Badge variant="outline" className="text-xs">{i.role}</Badge>
              <Badge variant="outline" className="text-xs">{i.used_at ? "used" : "pending"}</Badge>
              {!i.used_at && (
                <Button size="sm" variant="ghost" className="ml-auto h-7 px-2" onClick={() => revoke(i.id)} data-testid="invite-revoke">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {log.length > 0 && (
        <div className="mt-5 border-t border-border pt-5">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Recent security activity</p>
          <div className="mt-2 space-y-1 font-mono text-xs text-muted-foreground" data-testid="security-log">
            {log.map((e) => (
              <p key={e.id} className="truncate">
                {new Date(e.at).toLocaleString()} · {e.event} · {e.email || "—"} · {e.ip || "—"}
              </p>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
